/* eslint-disable no-restricted-globals */
/* eslint-disable no-alert */
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Row, Col, Card, CardBody, Button, Alert, CardHeader,
  Modal, ModalHeader, ModalBody,
  ModalFooter,
} from 'reactstrap';
import {
  Table, Thead, Tbody, Tr, Th, Td,
} from 'react-super-responsive-table';
import 'react-image-lightbox/style.css';
import { withTranslation } from 'react-i18next';

import moment from 'moment';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import {
  Formik, Field, Form,
} from 'formik';
import * as Yup from 'yup';
import { get, post, put } from '../../helpers/api_helper';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import BaseGrid from '../../components/Common/BaseGrid';
import AddressForm from '../../components/Order/AddressForm';
import { formatTime } from '../../utilities/timeFormat';

function Order(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const history = useHistory();

  const { t } = props;
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [patching, setPatching] = useState(false);
  const [packing, setPacking] = useState(false); // verb
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [order, setOrder] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const [shipments, setShipments] = useState([]);
  const [addressModal, setAddressModal] = useState(false);
  const [editAddress, setEditAddress] = useState(null);
  const [showEditShipment, setShowEditShipment] = useState(false);
  const [shipmentForEdit, setShipmentForEdit] = useState(null);

  const renderShippingAmount = (value) => {
    if (loading || order === null) return t('...');
    if (value == null || !Number.isFinite(Number(value))) return '--';
    return `$${Number(value).toFixed(2)}`;
  };

  const trackingNumberRef = useRef(null);
  const remainingNotPackedQty = order?.items?.reduce(
    (sum, item) => sum + Number(item.qty_unshipped || 0),
    0,
  ) || 0;
  const shortShippedLineCount = order?.items?.filter(
    (item) => Number(item.qty_unshipped || 0) > 0,
  ).length || 0;

  const copyToClipboard = async (value) => {
    if (!value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(String(value));
      toastr.success(t('Copied to clipboard'), t('Success'));
    } catch (err) {
      toastr.error(t('Unable to copy'), t('Error'));
    }
  };

  const renderCopyableValue = (value) => {
    if (!value) {
      return t('...');
    }

    return (
      <span className="d-inline-flex align-items-center gap-2">
        <span>{value}</span>
        <small>
          <button
            type="button"
            className="btn btn-link btn-sm p-0 border-0 text-muted align-baseline"
            title={t('Copy')}
            aria-label={t('Copy')}
            onClick={() => copyToClipboard(value)}
          >
            <i className="mdi mdi-content-copy font-size-14" aria-hidden="true" />
          </button>
        </small>
      </span>
    );
  };

  const formatStatusLogTimestamp = (timestamp) => {
    if (!timestamp) {
      return '';
    }

    const date = moment(timestamp);

    return (
      <span style={{ fontSize: '10px', lineHeight: 1.2, display: 'inline-block' }}>
        {date.format('ddd, M/D/YYYY h:mma')}
        <br />
        <strong>{date.fromNow()}</strong>
      </span>
    );
  };

  const editShipment = async (shipment) => {
    setShowEditShipment(true);
    setShipmentForEdit(shipment);
    setTimeout(() => {
      trackingNumberRef.current.focus();
    }, 300);
  };

  const fetchShipments = async () => {
    try {
      setLoading(true);
      const ents = await get(`/api/orders/shipment/get/order/${id}`);

      setShipments(ents);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const ent = await get(`/api/orders/${id}`);

      if (ent === '') {
        setOrder(null);
      } else {
        ent.shipments = [];
        setOrder(ent);
      }

      await fetchShipments();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const patchSale = async (values) => {
    try {
      setPatching(true);
      const result = await post(`/api/orders/save/${id}`, values);

      toastr.success(t(`Sale ID ${result.id} patched`), t('Success'));
      setOrder(result);
    } catch (e) {
      setError(e.message);
    } finally {
      setPatching(false);
    }
  };

  const togglePriority = async () => {
    if (order === null) {
      return;
    }

    try {
      setPatching(true);
      const result = await post(`/api/orders/save/${id}`, {
        priority: Number(order.priority) === 1 ? 0 : 1,
      });

      toastr.success(t(`Sale ID ${result.id} patched`), t('Success'));
      setOrder(result);
    } catch (e) {
      setError(e.message);
      toastr.error(e.message, t('Error'));
    } finally {
      setPatching(false);
    }
  };

  const patchShipment = async (shipmentId, data) => {
    try {
      setPatching(true);
      const upperedData = Object.fromEntries(
        Object.entries(data).map(([key, value]) => [key, String(value).toUpperCase()]),
      );

      const result = await put(`/api/orders/shipment/patch/${shipmentId}`, upperedData);

      toastr.success(t(`Sale ID ${result.id} patched`), t('Success'));
      setShowEditShipment(false);
      setShipmentForEdit(null);
      setRefresh(refresh + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setPatching(false);
    }
  };

  const createShipment = async (values) => {
    try {
      setPacking(true);

      // remove 0 qty items
      for (let i = 0; i < values.items.length; i += 1) {
        if (values.items[i].qty === 0) {
          values.items.splice(i, 1);
        }
      }

      if (values.items.length === 0) {
        throw new Error(t('At least one item is required'));
      }

      await post('/api/orders/shipment/create', values);
      toastr.success(t('Shipment created'), t('Success'));
      setRefresh(refresh + 1);
    } catch (e) {
      toastr.error(e.message, t('Error'));
    } finally {
      setPacking(false);
    }
  };

  const deleteShipment = async (shipment) => {
    if (window.confirm(t('Are you sure you want to delete this shipment?'))) {
      try {
        await post(`/api/orders/shipment/delete/${shipment.id}`, {});
        toastr.success(t('Shipment deleted'), t('Success'));
        setRefresh(refresh + 1);
      } catch (e) {
        toastr.error(e.message, t('Error'));
      }
    }
  };

  const next = () => {
    history.push(`/orders/${Number(id) + 1}`);
  };

  const prev = () => {
    history.push(`/orders/${Number(id) - 1}`);
  };

  const toggle = () => {
    setAddressModal(!addressModal);
  };

  useEffect(() => {
    fetchOrder();
  }, [id, refresh]);

  const voidOrder = async () => {
    if (window.confirm(t('Are you sure you want to void this order?'))) {
      try {
        await post(`/api/orders/save/${order.id}`, { void: true });

        toastr.success(`Order #${order.reference} successfully voided`, 'Success');
      } catch (err) {
        toastr.error(err.message, 'Error');
      } finally {
        setRefresh(refresh + 1);
      }
    }
  };

  const unvoidOrder = async () => {
    if (window.confirm(t('Are you sure you want to unvoid this order?'))) {
      try {
        await post(`/api/orders/save/${order.id}`, { void: false });

        toastr.success(`Order #${order.reference} successfully unvoided`, 'Success');
      } catch (err) {
        toastr.error(err.message, 'Error');
      } finally {
        setRefresh(refresh + 1);
      }
    }
  };

  const setAsShipped = async () => {
    if (window.confirm(t('Are you sure you want to mark this order as shipped?'))) {
      try {
        await post(`/api/orders/mark-shipped/${order.id}`, {});

        toastr.success(`Order #${order.reference} successfully marked as shipped`, 'Success');
      } catch (err) {
        toastr.error(err.message, 'Error');
      } finally {
        setRefresh(refresh + 1);
      }
    }
  };

  const unmarkAsShipped = async () => {
    if (window.confirm(t('Are you sure you want to unmark this order as shipped?'))) {
      try {
        await post(`/api/orders/save/${order.id}`, { shipped: false });

        toastr.success(`Order #${order.reference} successfully marked as unshipped`, 'Success');
      } catch (err) {
        toastr.error(err.message, 'Error');
      } finally {
        setRefresh(refresh + 1);
      }
    }
  };

  const repick = async () => {
    if (window.confirm(t('Are you sure you want to repick this order?'))) {
      try {
        await post(`/api/warehouse/pick/repick/${order.id}`);

        toastr.success(`Order #${order.reference} successfully reset for picking`, 'Success');
      } catch (err) {
        toastr.error(err.message, 'Error');
      } finally {
        setRefresh(refresh + 1);
      }
    }
  };

  const unship = async (shp) => {
    try {
      await post(`/api/orders/shipment/unship/${shp.id}`);

      setRefresh(refresh + 1);
    } catch (err) {
      toastr.error(err.message, t('Could not unship'));
    }
  };

  const commercialInvoice = async (shp) => {
    try {
      const authUser = await localStorage.getItem('authUser');
      const { token } = JSON.parse(authUser);

      window.open(`/api/orders/shipment/commercial-invoice/${shp.id}?token=${token}`, '_blank');
    } catch (err) {
      toastr.error(err.message, t('Error printing commercial invoice'));
    }
  };

  const packlist = async (shp) => {
    try {
      const authUser = await localStorage.getItem('authUser');
      const { token } = JSON.parse(authUser);

      window.open(`/api/warehouse/pack/packlist/${shp.id}?token=${token}`, '_blank');
    } catch (err) {
      toastr.error(err.message, t('Error printing commercial invoice'));
    }
  };

  return (
    <div className="page-content">
      <Breadcrumbs title="Orders" prevLink="/orders" breadcrumbItem={`Manage Order ID ${id}`} />

      <div className="container-fluid">
        <Row>
          <Col xl="7">
            <Card>
              <CardHeader className="text-uppercase bg-transparent border-bottom">
                {t('Order')}
                {' #'}
                {id}

                <div className="btn-group float-end">
                  <Button color="light" size="sm" title={t('Previous Order')} onClick={prev}>
                    <i className="fa fa-chevron-left" />
                  </Button>
                  <Button color="light" size="sm" title={t('Next Order')} onClick={next}>
                    <i className="fa fa-chevron-right" />
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {error !== null && (
                  <p className="text-danger">{error}</p>
                )}
                {error === null && (
                  <div>
                    <table className="table table-bordered table-striped responsiveTable">
                      <tbody>
                        <tr>
                          <th>{t('Imported')}</th>
                          <td>
                            <small>{loading !== true && order !== null && moment(order.created_at).format('ddd, M/D/YYYY h:mma')}</small>
                            <br />
                            <small>
                              <strong>{loading === true || order === null ? t('...') : formatTime(order.created_at)}</strong>
                            </small>
                          </td>
                        </tr>
                        <tr>
                          <th>{t('Reference')}</th>
                          <td>{loading === true || order === null ? t('...') : renderCopyableValue(order.reference)}</td>
                        </tr>
                        <tr>
                          <th>{t('Preferred Shipping Method')}</th>
                          <td>{loading === true || order === null ? t('...') : renderCopyableValue(order.ship_method)}</td>
                        </tr>
                        <tr>
                          <th>{t('Shipping Paid')}</th>
                          <td>{renderShippingAmount(order?.shipping_paid)}</td>
                        </tr>
                        <tr>
                          <th>{t('Calculated Shipping')}</th>
                          <td>{renderShippingAmount(order?.shipping_budget)}</td>
                        </tr>
                        <tr>
                          <th>{t('Priority')}</th>
                          <td>
                            {loading === true || order === null ? t('...') : (
                              <div className="form-check form-switch mb-0">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  role="switch"
                                  id="order-priority-switch"
                                  aria-label={t('Priority')}
                                  checked={Number(order.priority) === 1}
                                  disabled={patching}
                                  onChange={togglePriority}
                                />
                              </div>
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    {order !== null && order.void === 0 && order.shipped === 0 && (
                      <Button color="danger" size="sm" className="btn-soft-danger" onClick={voidOrder}>
                        <span className="mdi mdi-close-thick" />
                        {' '}
                        {t('Void Order')}
                      </Button>
                    )}

                    {order !== null && order.void === 1 && (
                      <Button color="success" size="sm" className="btn-soft-success" onClick={unvoidOrder}>
                        <span className="mdi mdi-backup-restore" />
                        {' '}
                        {t('Unvoid Order')}
                      </Button>
                    )}

                    {order !== null && order.shipped === 0 && order.void === 0 && (
                      <Button color="info" size="sm" className="btn-soft-info ms-2" onClick={setAsShipped}>
                        <span className="mdi mdi-truck" />
                        {' '}
                        {t('Mark Shipped')}
                      </Button>
                    )}

                    {order !== null
                      && order.void === 0
                      && order.shipped === 1
                      && remainingNotPackedQty > 0 && (
                      <Button color="warning" size="sm" className="btn-soft-warning ms-2" onClick={unmarkAsShipped}>
                        <span className="mdi mdi-truck-fast-outline" />
                        {' '}
                        {t('Unmark Shipped')}
                      </Button>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>

            {loading !== true && order !== null && order.void === 1 && (
              <Alert color="danger">
                <strong className="alert-heading">{t('Order has VOID status')}</strong>
              </Alert>
            )}

            {loading !== true
              && order !== null
              && order.shipped === 1
              && remainingNotPackedQty > 0 && (
              <Alert color="info">
                <strong className="alert-heading">Short shipped</strong>
                <div>
                  {`This order is marked shipped with ${remainingNotPackedQty} unit(s)
                  still not packed across ${shortShippedLineCount} line item(s).`}
                </div>
              </Alert>
            )}

            {loading !== true && order !== null && order.customer_note !== null && order.customer_note !== '' && (
              <Card className="border border-success">
                <CardHeader className="text-uppercase bg-transparent border-bottom">{t('Customer Note')}</CardHeader>
                <CardBody>
                  <blockquote>{order !== null ? order.customer_note : '-'}</blockquote>
                </CardBody>
              </Card>
            )}

            {loading !== true && order !== null && (
              <>
                <Card>
                  <CardHeader className="text-uppercase bg-transparent border-bottom">{t('Staff Note')}</CardHeader>
                  <CardBody>
                    <Formik
                      enableReinitialize
                      initialValues={{ staff_note: order.staff_note ? order.staff_note : '' }}
                      validationSchema={Yup.object().shape({
                        staff_note: Yup
                          .string(),
                      })}
                      onSubmit={patchSale}
                    >
                      {({
                        errors, setFieldValue,
                      }) => (
                        <Form id="OrderForm" autoComplete="off">
                          {Object.entries(errors).length > 0 && (
                            <Alert color="danger">{t('Form contains errors')}</Alert>
                          )}
                          <div className="mb-3">
                            <Field className="form-control" id="staff_note" as="textarea" name="staff_note" onChange={(e) => { setFieldValue('staff_note', e.target.value); }} />
                          </div>

                          <Button type="submit" size="sm" color="info" className="float-end btn-soft-primary" disabled={patching}>{patching ? t('Saving...') : t('Save')}</Button>
                        </Form>
                      )}
                    </Formik>
                  </CardBody>
                </Card>

                {process.env.REACT_APP_STATIC_INVENTORY !== '1' && (
                  <Card>
                    <CardHeader className="text-uppercase bg-transparent border-bottom">{t('Pick Routes')}</CardHeader>
                    <CardBody>
                      {order !== null && order.shipped === 0 && order.void === 0 && (
                        <Button className="mb-3 p-1" size="sm" color="danger" onClick={repick}>
                          <span className="mdi mdi-refresh" />
                          {' '}
                          {t('Repick')}
                        </Button>
                      )}
                      <BaseGrid
                        url="/api/warehouse/pick/items"
                        shouldUpdate={refresh}
                        filters={`oi.order_id=${order.id}`}
                        config={
                          {
                            columns: {
                              id: {
                                label: 'Route',
                                sort: true,
                                format: (y, x) => x.pickroute.id,
                                link: {
                                  to: '/warehouse/pickroute/%pickroute_id%',
                                },
                              },
                              route_item: {
                                label: 'Pick Item',
                                format: (y, x) => x.id,
                              },
                              location: {
                                label: 'Location',
                                format: (y, x) => x.location.label,
                              },
                              qty: {
                                label: 'Qty',
                              },
                              part_number: {
                                label: 'Part Number',
                                format: (y, x) => {
                                  if (x.inventory === null) {
                                    return `KIT-${x.stock_id}`;
                                  }

                                  return x.inventory.part_number;
                                },
                              },
                              status: {
                                label: 'Status',
                                html: true,
                                format: (y, x) => {
                                  if (x.picked_at === null && !x.void && x.pickroute.ready) {
                                    return `<span class="badge bg-primary">${t('Picking...')}</span>`;
                                  }

                                  if (x.picked_at !== null && !x.void) {
                                    return '<span class="badge bg-success">Picked</span>';
                                  }

                                  return '<span class="badge bg-danger bg-soft">Voided</span>';
                                },
                              },
                              picked_at: {
                                label: 'Picked',
                                date: {
                                  format: (date) => (moment(date).fromNow()),
                                  timezone: process.env.REACT_APP_LOCAL_TZ,
                                },
                              },
                              picked_by: { label: 'By' },
                              cart: {
                                label: 'Cart',
                              },
                              void_reason: {
                                label: 'Message',
                                style: { minWidth: '100px' },
                                html: true,
                                format: (f, ent) => (f ? `${f}<br />${ent.voided_by}` : ''),
                              },
                            },
                            pagination: {
                              sort: 'id',
                              direction: 'desc',
                              perPage: 10,
                            },
                          }
                        }
                      />
                    </CardBody>
                  </Card>
                )}

                <Card>
                  <CardHeader className="text-uppercase bg-transparent border-bottom">{t('Order Status Log')}</CardHeader>
                  <CardBody>
                    <BaseGrid
                      url={`/api/orders/${order.id}/status-log`}
                      shouldUpdate={refresh}
                      hasSearch
                      config={
                        {
                          columns: {
                            timestamp: {
                              label: 'Timestamp',
                              date: {
                                format: (date) => formatStatusLogTimestamp(date),
                                timezone: process.env.REACT_APP_LOCAL_TZ,
                              },
                            },
                            action: {
                              label: 'Action',
                            },
                            user_name: {
                              label: 'User',
                            },
                          },
                          pagination: {
                            sort: 'timestamp',
                            direction: 'asc',
                            perPage: 10,
                          },
                        }
                      }
                    />
                  </CardBody>
                </Card>
              </>
            )}
          </Col>
          <Col xl="5">
            {order !== null && (
              <div>
                <Row>
                  <Col xl="10">
                    <Modal isOpen={addressModal} toggle={toggle} size="md">
                      <ModalHeader toggle={toggle}>{t('Edit Address')}</ModalHeader>
                      <ModalBody>
                        <AddressForm
                          entity={editAddress}
                          actionUrl="/api/orders/address"
                          onSuccess={() => { setAddressModal(false); setRefresh(refresh + 1); }}
                        />
                      </ModalBody>
                      <ModalFooter>
                        <Button color="dark" onClick={toggle}>{t('Cancel')}</Button>
                        <Button color="secondary" form="AddressForm" type="submit">{t('Save')}</Button>
                      </ModalFooter>
                    </Modal>

                    {order.soldTo !== null && (
                      <Card>
                        {
                          (order.soldTo.id === order.shipTo.id)
                          && (
                            <CardHeader className="text-uppercase bg-transparent border-bottom">{t('Sold To/Ship To')}</CardHeader>
                          )
                        }
                        {
                          (order.soldTo.id !== order.shipTo.id)
                          && (
                            <CardHeader className="text-uppercase bg-transparent border-bottom">{t('Sold To')}</CardHeader>
                          )
                        }

                        <CardBody>
                          <div className="float-end" style={{ width: '64px', height: '64px' }}>
                            <span className="avatar-title rounded-circle bg-soft bg-success text-dark font-size-24">
                              <span className="mdi mdi-card-account-details-outline" />
                            </span>
                          </div>

                          <address>
                            <strong>{`${order.soldTo.firstname} ${order.soldTo.lastname}`}</strong>
                            <br />
                            <span>{order.soldTo.address_1}</span>
                            <br />
                            {order.soldTo.address_2 && (
                              <>
                                <span>{order.soldTo.address_2}</span>
                                <br />
                              </>
                            )}
                            <span>{`${order.soldTo.city} ${order.soldTo.region} ${order.soldTo.postcode}`}</span>
                            <br />
                            <span>{order.soldTo.country}</span>
                            <br />
                            <br />
                            <span className="mdi mdi-office-building-marker" />
                            {' '}
                            <span>{order.soldTo.company ? order.soldTo.company : '--'}</span>
                            <br />
                            <span className="mdi mdi-phone" />
                            {' '}
                            <span>{order.soldTo.telephone ? order.soldTo.telephone : '--'}</span>
                            <br />
                            <Button color="primary" size="sm" className="btn-soft-primary mt-3" onClick={() => { setEditAddress(order.shipTo); setAddressModal(true); }}>
                              <span className="mdi mdi-pencil" />
                              {' '}
                              {t('Edit')}
                            </Button>
                          </address>
                        </CardBody>
                      </Card>
                    )}

                    {
                      order.shipTo
                      && (order.soldTo === null || order.shipTo.id !== order.soldTo.id) && (
                        <Card>
                          <CardHeader className="text-uppercase bg-transparent border-bottom">{t('Ship To')}</CardHeader>
                          <CardBody>
                            <div className="float-end" style={{ width: '64px', height: '64px' }}>
                              <span className="avatar-title rounded-circle bg-soft bg-success text-success font-size-24">
                                <span className="mdi mdi-card-account-details-outline" />
                              </span>
                            </div>

                            <address>
                              <strong>{`${order.shipTo.firstname} ${order.shipTo.lastname}`}</strong>
                              <br />
                              <span>{order.shipTo.address_1}</span>
                              <br />
                              {order.shipTo.address_2 && (
                                <>
                                  <span>{order.shipTo.address_2}</span>
                                  <br />
                                </>
                              )}
                              <span>{`${order.shipTo.city} ${order.shipTo.region} ${order.shipTo.postcode}`}</span>
                              <br />
                              <span>{order.shipTo.country}</span>
                              <br />
                              <br />
                              <span className="mdi mdi-office-building-marker" />
                              {' '}
                              <span>{order.shipTo.company ? order.shipTo.company : '--'}</span>
                              <br />
                              <span className="mdi mdi-phone" />
                              {' '}
                              <span>{order.shipTo.telephone ? order.shipTo.telephone : '--'}</span>
                              <br />
                              <Button color="primary" size="sm" className="btn-soft-primary mt-3" onClick={() => { setEditAddress(order.shipTo); setAddressModal(true); }}>
                                <span className="mdi mdi-pencil" />
                                {' '}
                                {t('Edit')}
                              </Button>
                            </address>
                          </CardBody>
                        </Card>
                      )
                    }
                  </Col>
                </Row>
                <Row>
                  <Col xl="12">
                    {shipments.length > 0 && (
                      <div>
                        {shipments.map((shipment) => {
                          const hasOtherOrderItems = (shipment.items || []).some(
                            (item) => item?.order_item?.order_id !== order.id,
                          );

                          return (
                            <Card key={shipment.id}>
                              <CardHeader className="text-uppercase bg-transparent border-bottom">{t(`LPN ${shipment.lpn}`)}</CardHeader>
                              <CardBody>
                                <div>
                                  <div className="table-responsive">
                                    <Table className="table table-bordered table-sm">
                                      <Tbody>
                                        <Tr>
                                          <Th>{t('Service')}</Th>
                                          <Td>{shipment.service}</Td>
                                        </Tr>
                                        <Tr>
                                          <Th>{t('Carrier')}</Th>
                                          <Td>{shipment.carrier}</Td>
                                        </Tr>
                                        <Tr>
                                          <Th>{t('Dimensions')}</Th>
                                          <Td>{shipment.dimensions}</Td>
                                        </Tr>
                                        <Tr>
                                          <Th>{t('Weight')}</Th>
                                          <Td>{`${shipment.weight} lbs`}</Td>
                                        </Tr>
                                        <Tr>
                                          <Th>{t('Tracking Number')}</Th>
                                          <Td>{shipment.tracking_number}</Td>
                                        </Tr>
                                        <Tr>
                                          <Th>{t('ZPL HEX ID')}</Th>
                                          <Td>
                                            {(shipment.shipping_labels || [])
                                              .map((label) => label.zpl_hex_id)
                                              .filter(Boolean)
                                              .join(', ') || '-'}
                                          </Td>
                                        </Tr>
                                        <Tr>
                                          <Th>{t('Return Label')}</Th>
                                          <Td>{shipment.pour_label}</Td>
                                        </Tr>
                                        {hasOtherOrderItems && (
                                          <Tr>
                                            <Th>{t('Consolidated')}</Th>
                                            <Td>{t('This shipment also contains items from another order.')}</Td>
                                          </Tr>
                                        )}
                                        <Tr>
                                          <Th>{t('Packed')}</Th>
                                          <Td>
                                            <small>{`${moment(shipment.created_at).format('ddd, M/D/YYYY h:mma')} by ${shipment.packed_by}`}</small>
                                            <br />
                                            <small>
                                              <strong>{formatTime(shipment.created_at)}</strong>
                                            </small>
                                          </Td>
                                        </Tr>
                                        {shipment.shipped_at !== null && (
                                          <Tr>
                                            <Th>{t('Shipped')}</Th>
                                            <Td>
                                              <small>{`${moment(shipment.shipped_at).format('ddd, M/D/YYYY h:mma')} by ${shipment.shipped_by}`}</small>
                                              <br />
                                              <small>
                                                <strong>
                                                  {formatTime(shipment.shipped_at)}
                                                </strong>
                                              </small>
                                            </Td>
                                          </Tr>
                                        )}
                                      </Tbody>
                                    </Table>
                                  </div>

                                  <div className="table-responsive">
                                    <Table className=" mt-3 table table-bordered">
                                      <Thead>
                                        <Tr>
                                          <Th>{t('Order')}</Th>
                                          <Th>{t('Part Number')}</Th>
                                          <Th>{t('Lot')}</Th>
                                          <Th>{t('Serial')}</Th>
                                          <Th>{t('Qty')}</Th>
                                        </Tr>
                                      </Thead>
                                      <Tbody>
                                        {shipment.items.map((item) => {
                                          const itemOrderId = item?.order_item?.order_id;
                                          const isOtherOrder = Number(itemOrderId)
                                            !== Number(order.id);
                                          let orderLink = '--';

                                          if (item.order_item?.order) {
                                            orderLink = (
                                              <div className="d-flex flex-column">
                                                <Link to={`/orders/${item.order_item.order.id}`}>
                                                  {item.order_item.order.reference}
                                                </Link>
                                                <small className="text-muted">
                                                  #
                                                  {item.order_item.order.id}
                                                </small>
                                              </div>
                                            );
                                          } else if (itemOrderId) {
                                            orderLink = (
                                              <div className="d-flex flex-column">
                                                <Link to={`/orders/${itemOrderId}`}>
                                                  #
                                                  {itemOrderId}
                                                </Link>
                                              </div>
                                            );
                                          }

                                          return (
                                            <Tr key={item.id} className={isOtherOrder ? 'table-warning' : ''}>
                                              <Td>{orderLink}</Td>
                                              <Td>
                                                {item.order_item.inventory_part_number}
                                                {' '}
                                                {item.order_item.inventory_description}
                                              </Td>
                                              <Td>{item.lot}</Td>
                                              <Td>{item.serial}</Td>
                                              <Td>{item.qty}</Td>
                                            </Tr>
                                          );
                                        })}
                                      </Tbody>
                                    </Table>
                                  </div>

                                  {shipment.shipped_at === null && (
                                    <div>
                                      <Button color="danger" className="btn-soft-danger float-end" onClick={() => { deleteShipment(shipment); }}>
                                        <span className="mdi mdi-close-thick" />
                                        {' '}
                                        {t('Remove')}
                                      </Button>

                                      {order !== null && order.void === 0 && (
                                        <div className="hstack gap-1">
                                          <Link to={`/ship/${shipment.lpn}`} className="btn btn-primary btn-soft-primary">
                                            <span className="mdi mdi-truck-fast" />
                                            {' '}
                                            {t('Ship!')}
                                          </Link>

                                          <Button
                                            className="btn btn-sm btn-primary btn-soft-primary"
                                            onClick={() => {
                                              editShipment(shipment);
                                            }}
                                          >
                                            <span className="mdi mdi-truck-fast" />
                                            {' '}
                                            {t('Enter Details Manually')}
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {shipment.shipped_at !== null && (
                                    <div className="hstack gap-2">
                                      {shipment.exported_at === null && (
                                        <Button color="danger" className="btn-soft-danger" onClick={() => { unship(shipment); }}>
                                          <span className="mdi mdi-truck" />
                                          {' '}
                                          {t('Unship')}
                                        </Button>
                                      )}

                                      <Button color="primary" size="sm" className="btn-soft-primary" onClick={() => { commercialInvoice(shipment); }}>
                                        <span className="mdi mdi-printer" />
                                        {' '}
                                        {t('Commercial Invoice')}
                                      </Button>

                                      <Button color="primary" size="sm" className="btn-soft-primary" onClick={() => { packlist(shipment); }}>
                                        <span className="mdi mdi-printer" />
                                        {' '}
                                        {t('Packlist')}
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </CardBody>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </Col>
                </Row>
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col xl="12">
            <Card>
              <CardHeader className="text-uppercase bg-transparent border-bottom">{t('Items')}</CardHeader>
              <CardBody>
                <Formik
                  enableReinitialize
                  initialValues={order !== null ? {
                    order_id: order.id,
                    items: order.items.map((item) => ({
                      order_item_id: item.id, qty: item.qty_unshipped,
                    })),
                  } : { order_id: null, items: [] }}
                  validationSchema={Yup.object().shape({
                    order_id: Yup.number().required(),
                    items: Yup.array().of(Yup.object().shape({
                      order_item_id: Yup.number().required(),
                      qty: Yup.number().required(),
                    })).min(1, 'At least one item is required'),
                  })}
                  onSubmit={(values) => {
                    createShipment(values);
                  }}
                >
                  {({
                    // eslint-disable-next-line no-unused-vars
                    touched, errors, setFieldValue,
                  }) => (
                    <Form id="CreateShipmentForm" autoComplete="off">
                      <div className="table-responsive">
                        <Table className="table table-striped table-bordered">
                          <Thead>
                            <Tr>
                              <Th>{t('Package Qty')}</Th>
                              <Th>{t('Photo')}</Th>
                              <Th>{t('Stock ID')}</Th>
                              <Th>{t('Part Number')}</Th>
                              <Th>{t('Description')}</Th>
                              <Th>{t('Qty')}</Th>
                              <Th>{t('Qty Not Packed')}</Th>
                              <Th>{t('Value')}</Th>
                              <Th>{t('Total')}</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {order !== null && order.items.map((item, k) => (
                              <Tr key={item.id}>
                                <Td>
                                  <Field type="number" disabled={order.void === 1} max={item.qty_unshipped} min="0" name={`items[${k}].qty`} className="form-control" style={{ maxWidth: '70px' }} />
                                </Td>
                                <Td>
                                  {item.inventory !== null && (
                                    <Link to={`/inventory/${item.inventory.id}`}>
                                      <img
                                        src={`/api/inventory/thumbnail/${item.inventory.id}`}
                                        alt="Item"
                                        style={{
                                          cursor: 'pointer', width: '100%', maxWidth: '64px', maxHeight: '64px',
                                        }}
                                      />
                                    </Link>
                                  )}
                                </Td>
                                <Td className="align-middle">
                                  {item.inventory !== null && (
                                    <Link to={`/inventory/${item.inventory.id}`}>{item.inventory.stock_id}</Link>
                                  )}
                                  {item.inventory === null && (
                                    t('Not linked')
                                  )}
                                </Td>
                                <Td className="align-middle">
                                  {item.inventory !== null && (
                                    <Link to={`/inventory/${item.inventory.id}`}>
                                      {item.inventory_part_number}
                                      {item.substituted === 1 && (
                                        <>
                                          <br />
                                          <small className="text-info">
                                            {t('Substitute')}
                                          </small>
                                        </>
                                      )}
                                    </Link>
                                  )}
                                  <span>
                                    {item.inventory === null && (
                                      item.inventory_part_number
                                    )}
                                  </span>
                                </Td>
                                <Td className="align-middle">{item.inventory_description}</Td>
                                <Td className="align-middle">{item.qty}</Td>
                                <Td className="align-middle">{item.qty_unshipped}</Td>
                                <Td className="align-middle">
                                  {item.value}
                                  {' '}
                                  {item.currency_code}
                                </Td>
                                <Td className="align-middle">
                                  {item.qty * item.value}
                                  {' '}
                                  USD
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </div>

                      {order !== null && order.void === 0 && (
                        <>
                          <Button type="submit" color="primary" className="btn-soft-primary" disabled>
                            <span className="mdi mdi-package-variant-closed" />
                            {' '}
                            {packing ? t('Packing...') : t('Simple Packout')}
                          </Button>
                          <Button color="danger" className="btn-soft-danger ms-3" onClick={() => { history.push(`/warehouse/pack/v2/${id}`); }}>
                            {' '}
                            <span className="mdi mdi-package-variant-closed" />
                            {' '}
                            {t('Pack Page')}
                          </Button>
                        </>
                      )}
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {shipmentForEdit && (
          <Modal size="md" isOpen={showEditShipment}>
            <ModalBody>
              <Formik
                enableReinitialize
                initialValues={{
                  tracking_number: shipmentForEdit.tracking_number,
                  carrier: shipmentForEdit.carrier || 'UPS',
                  service: shipmentForEdit.service,
                }}
                validationSchema={Yup.object().shape({
                  tracking_number: Yup.string().required(),
                  carrier: Yup.string().required().max(45),
                  service: Yup.string().max(45).nullable(),
                })}
                onSubmit={(data) => {
                  patchShipment(shipmentForEdit.id, data);
                }}
              >
                {({
                  errors,
                }) => (
                  <Form id="editShipmentForm" autoComplete="off">
                    {Object.entries(errors).length > 0 && (
                      <Alert color="danger">{t('Form contains errors')}</Alert>
                    )}

                    <Row>
                      <Col md="6">
                        <label htmlFor="carrier">{t('Carrier')}</label>
                        <Field className="form-control" id="carrier" name="carrier" />
                      </Col>
                      <Col md="6">
                        <label htmlFor="service">{t('Service')}</label>
                        <Field className="form-control" id="service" name="service" />
                      </Col>
                    </Row>

                    <div className="mt-3">
                      <label htmlFor="tracking_number">{t('Tracking or BOL')}</label>
                      <Field className="form-control" innerRef={trackingNumberRef} id="tracking_number" name="tracking_number" />
                    </div>
                  </Form>
                )}
              </Formik>
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={() => { setShowEditShipment(false); }}>{t('Close')}</Button>
              <Button type="submit" form="editShipmentForm" color="primary">{t('Save')}</Button>
            </ModalFooter>
          </Modal>
        )}
      </div>
    </div>
  );
}

Order.propTypes = {
  // eslint-disable-next-line react/require-default-props
  t: PropTypes.any,
};

const mapStateToProps = (state) => ({ ...state.Layout });

export default connect(mapStateToProps, {})(withTranslation()(Order));
